import React, { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Modal, Progress, Form, Select } from 'antd';
import ImportDsRequestService from '../../../services/importDsRequest.service';

import useNotify from '../../../context/Notification/useNotify';
import GlobalService from '../../../services/Global.service';
import usePermission from '../../../hooks/usePermission';

const { Option } = Select;

export default function ImportData({ onSearch, currentPage }) {
    const importRef = useRef();
    const { t } = useTranslation(['import-ds-request']);
    const notify = useNotify();
    const [downloading, setDownloading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [percent, setPercent] = useState();
    const [form] = Form.useForm();
    const [domainList, setDomainList] = useState([]);

    useEffect(() => {
        const getDomainList = async () => {
            try {
                const { data } = await GlobalService.getDomainVerify();
                if (data?.resultData) {
                    const item = data?.resultData;
                    setDomainList(item?.filter((e) => e !== null) || []);
                }
            } catch (error) {
                console.log(error);
            }
        };
        getDomainList();
    }, []);

    const DownloadTemplate = async (values) => {
        setDownloading(true);
        try {
            const res = await ImportDsRequestService.DownloadTemplate(
                values.doamin
            );
            setDownloading(false);
            const objectURL = window.URL.createObjectURL(new Blob([res?.data]));
            const link = document.createElement('a');
            const filename =
                res.headers['content-disposition'].split(
                    "filename*=utf-8''"
                )[1];
            link.href = objectURL;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            form.resetFields();
        } catch (error) {
            setDownloading(false);
        }
    };

    const onInputClick = (event) => {
        event.target.value = '';
    };

    // File Type
    const allowedType = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
    ];

    const onUploadProgress = ({ total, loaded }) => {
        const percent = Math.round((loaded / total) * 100).toFixed(2);
        setPercent(percent);
    };

    const ImportDsRequest = async (e) => {
        setUploading(true);
        const files = e.target.files;
        let size = 0;
        let bodyFormDocData = new FormData();
        for (let i = 0; i < files.length; i++) {
            const fileType = files[i].type;
            size += files[i].size;
            if (!allowedType.includes(fileType)) {
                notify({
                    type: 'import-fail',
                    title: t('Import Failed'),
                    closeable: true,
                    description: t('AllowedFileType'),
                });
                setUploading(false);
                return;
            } else {
                const formfile = e.target.files[i];
                const utf8Name = encodeURIComponent(formfile.name);
                const header = `form-data; name="uploadFile"; filename*=UTF-8''${utf8Name}`;
                bodyFormDocData.append('file', formfile, header);
            }
        }
        const fileSize = size / 1024 / 1024;
        if (fileSize <= 10) {
            try {
                bodyFormDocData.append('action', 'import-ds-request');
                const { data } = await ImportDsRequestService.UploadDsRequest(
                    bodyFormDocData,
                    onUploadProgress
                );
                const { resultMsg, resultData } = data;
                setUploading(false);
                if (resultMsg !== 'FAILURE') {
                    onSearch({
                        ...currentPage,
                        p: 1,
                        s: 'importDate',
                        sd: 'desc',
                    });
                    if (resultData?.importFail === 0) {
                        notify({
                            type: 'import-success',
                            timeOut: 2500,
                            title: t('Import Finished'),
                            description: t('Successfully imported data'),
                        });
                    }
                } else {
                    if (resultData?.msg === 'Data not found in file imported') {
                        notify({
                            type: 'import-fail',
                            title: t('Import Failed'),
                            closeable: true,
                            description: t('Data not found in file imported'),
                        });
                    } else {
                        notify({
                            type: 'import-fail',
                            title: t('Import Failed'),
                            closeable: true,
                            description: t(
                                'Unexpected error has occurred while upload'
                            ),
                        });
                    }
                }
            } catch (error) {
                setUploading(false);
                notify({
                    type: 'import-fail',
                    title: t('Import Failed'),
                    closeable: true,
                    description: t(
                        'Unexpected error has occurred while upload'
                    ),
                });
            }
        } else {
            setUploading(false);
            notify({
                type: 'import-fail',
                title: t('Import Failed'),
                closeable: true,
                description: t('MaxFile'),
            });
        }
    };

    const { isAuditor, isUser } = usePermission();

    return (
        <>
            <div className="text-xl font-medium">{t('Import data')}</div>
            <div className="text-[13px] text-[#B5B5C3]">
                {t('Transform Data to Powerful System')}
            </div>
            <div
                className="my-3"
                style={{ borderBottom: '1px solid #EDEEF9' }}
            ></div>
            <div className="grid grid-cols-2 gap-4">
                <div className="col-span-1">
                    <div className="p-4 border-[#A9B2FF] border border-solid rounded-lg h-full">
                        <div className="flex flex-col items-center">
                            <div className="text-xl font-medium">
                                {t('Download Template Form')}
                            </div>
                            <div className="my-2">{IconDownload}</div>

                            <div className="my-6 w-7/12">
                                <Form
                                    scrollToFirstError={{
                                        behavior: 'smooth',
                                        block: 'center',
                                    }}
                                    form={form}
                                    onFinish={DownloadTemplate}
                                    layout="vertical"
                                    className="space-y-2"
                                    disabled={isAuditor || isUser}
                                >
                                    <Form.Item
                                        className="mt-4"
                                        name="doamin"
                                        label={t('Select domain')}
                                        key="doamin"
                                        rules={[
                                            {
                                                required: true,
                                                message: t(
                                                    'This field is required'
                                                ),
                                            },
                                        ]}
                                    >
                                        <Select
                                            className=" w-full"
                                            placeholder={t(
                                                'Please select domain'
                                            )}
                                            size="large"
                                            showSearch
                                            optionFilterProp="children"
                                            filterOption={(input, option) =>
                                                (option?.children ?? '')
                                                    .toLowerCase()
                                                    .includes(
                                                        input.toLowerCase()
                                                    )
                                            }
                                        >
                                            {domainList?.map((res) => (
                                                <Option
                                                    key={res?.domainId}
                                                    value={res?.domainId}
                                                >
                                                    {res?.domainName}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                    <Form.Item className="text-center">
                                        <Button
                                            className="min-w-[200px] font-medium text-sm"
                                            htmlType="submit"
                                            size="large"
                                            type="primary"
                                            loading={downloading}
                                            // onClick={() => DownloadTemplate()}
                                        >
                                            {t('Download Template')}
                                        </Button>
                                    </Form.Item>
                                </Form>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-span-1">
                    <div className="p-4 border-[#A9B2FF] border border-solid rounded-lg h-full">
                        <div className="flex flex-col items-center">
                            <div className="text-xl font-medium">
                                {t('Import Template')}
                            </div>
                            <div className="my-2">{IconImport}</div>
                            <ul className="text-xs text-dark whitespace-pre-line">
                                <li key={1}>{t('Upload rule')}</li>
                                <li key={2}>{t('Max rule')}</li>
                                <li key={3}>{t('Allow rule')}</li>
                            </ul>
                            <label
                                onClick={() => importRef.current.click()}
                                className={'cursor-pointer my-6'}
                            >
                                <Button
                                    disabled={isAuditor || isUser}
                                    className="min-w-[200px] font-medium text-sm"
                                    size="large"
                                    type="primary"
                                    loading={uploading}
                                >
                                    {t('Import File')}
                                </Button>
                                <input
                                    ref={importRef}
                                    onChange={ImportDsRequest}
                                    onClick={onInputClick}
                                    type="file"
                                    multiple
                                    style={{ display: 'none' }}
                                    accept={allowedType.join(',')}
                                    // value={valueInput}
                                />
                            </label>
                        </div>
                    </div>
                </div>
            </div>
            <Modal
                open={uploading}
                onCancel={() => setUploading(false)}
                centered
                footer={false}
                closable={false}
                className="_modal"
            >
                <Progress
                    strokeColor={{
                        '0%': '#E6E6FA',
                        '100%': '#7D7AFF',
                    }}
                    percent={percent}
                    showInfo={false}
                />
                <p className="text-[#65707A] text-tiny font-semibold text-center ">
                    {t('ระบบกำลังตรวจสอบข้อมูลไฟล์')}...
                </p>
            </Modal>
        </>
    );
}

const IconDownload = (
    <svg
        width="232"
        height="122"
        viewBox="0 0 232 122"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <g clipPath="url(#clip0_3378_75846)">
            <path
                d="M208.342 88.4778C207.131 96.1742 203.379 103.645 197.188 108.372C191.095 113.028 183.289 114.682 175.753 116.104C166.715 117.812 157.652 119.373 148.564 120.787C147.276 120.992 145.987 121.19 144.696 121.382H91.7645C91.7109 121.31 91.6544 121.236 91.6009 121.162C91.5146 121.037 91.4283 120.912 91.3449 120.787C91.2974 120.715 91.2498 120.641 91.2021 120.567H124.404C125.346 120.566 126.249 120.191 126.916 119.525C127.582 118.86 127.957 117.957 127.959 117.014V72.6981C128.173 72.0258 128.372 71.3504 128.554 70.6662C129.401 67.4799 129.951 64.2223 130.199 60.9348C130.378 58.644 130.431 56.3532 130.392 54.0595C130.282 47.7554 129.458 41.4512 128.563 35.1917C128.497 34.7306 128.429 34.2665 128.363 33.8054C127.733 29.4559 127.159 24.7226 129.351 20.9115C131.859 16.5501 137.425 14.7323 142.438 15.1786C151.456 15.9848 159.051 22.8899 162.999 31.0386C166.944 39.1843 167.827 48.4485 168.187 57.4927C168.378 62.2885 168.357 67.2984 167.688 72.1091C169.949 66.9385 172.212 61.7678 174.477 56.5972C175.685 53.8393 176.955 50.9922 179.192 48.9751C183.869 44.7535 191.604 45.6044 196.599 49.4482C201.591 53.2949 204.328 59.3729 206.149 65.4033C208.404 72.8617 209.556 80.7813 208.342 88.4778Z"
                fill="#F2F2F2"
            />
            <path
                d="M188.393 46.6743C191.664 52.4662 193.164 59.1077 193.128 65.7353C193.077 72.3064 191.624 78.7908 188.864 84.7546C186.368 90.1396 182.891 95.0124 178.61 99.1237C174.386 103.226 169.373 106.428 163.875 108.537C157.495 110.928 150.595 111.584 143.879 110.437C142.946 110.277 142.02 110.082 141.101 109.854C140.543 109.716 140.305 110.577 140.863 110.714C147.852 112.447 155.181 112.236 162.058 110.105C167.786 108.262 173.072 105.255 177.583 101.273C182.15 97.2801 185.927 92.4651 188.717 87.0784C191.77 81.1809 193.552 74.7081 193.946 68.0788C194.336 61.3517 193.208 54.5048 190.299 48.4025C189.946 47.6631 189.568 46.9368 189.164 46.2238C189.102 46.126 189.005 46.0559 188.892 46.0285C188.78 46.001 188.661 46.0183 188.561 46.0768C188.461 46.1352 188.388 46.2302 188.356 46.3416C188.325 46.4531 188.338 46.5724 188.393 46.6743Z"
                fill="white"
            />
            <path
                d="M141.028 15.0607C144.654 21.7448 147.48 28.8329 149.447 36.1783C153.384 50.8902 153.977 66.2972 151.182 81.2682C149.786 88.748 147.511 96.0365 144.402 102.981C141.302 109.868 136.839 117.124 129.217 119.535C128.356 119.806 127.475 120.003 126.581 120.124C126.014 120.202 126.255 121.062 126.818 120.985C134.703 119.902 140.183 113.351 143.619 106.674C147.174 99.764 149.706 92.2607 151.39 84.6853C154.726 69.5698 154.606 53.896 151.039 38.8334C149.266 31.3298 146.617 24.0607 143.146 17.1761C142.71 16.3139 142.261 15.4586 141.798 14.6102C141.524 14.1055 140.753 14.5555 141.028 15.0607Z"
                fill="white"
            />
            <path
                d="M103.706 13.7623H50.2743C49.1898 13.7637 48.1501 14.1951 47.3833 14.962C46.6164 15.7288 46.185 16.7685 46.1836 17.853V44.6254H54.4067C55.0081 44.6238 55.6 44.7757 56.1263 45.0667C56.6526 45.3577 57.096 45.7783 57.4144 46.2885L60.2645 50.8254L62.9897 55.1601L63.5013 55.9752L66.6192 60.9346L66.6549 60.9911H107.797V17.853C107.795 16.7685 107.364 15.7288 106.597 14.962C105.83 14.1951 104.791 13.7637 103.706 13.7623Z"
                fill="#E9E9E9"
            />
            <path
                d="M82.5271 21.9469H56.0521C55.3502 21.9469 54.6771 22.2257 54.1808 22.722C53.6846 23.2182 53.4058 23.8913 53.4058 24.5932C53.4058 25.295 53.6846 25.9681 54.1808 26.4644C54.6771 26.9607 55.3502 27.2395 56.0521 27.2395H82.5271C83.2289 27.2395 83.902 26.9607 84.3983 26.4644C84.8946 25.9681 85.1734 25.295 85.1734 24.5932C85.1734 23.8913 84.8946 23.2182 84.3983 22.722C83.902 22.2257 83.2289 21.9469 82.5271 21.9469Z"
                fill="white"
            />
            <path
                d="M97.2088 34.941H56.2931C55.5269 34.941 54.7921 35.2454 54.2504 35.7871C53.7086 36.3289 53.4043 37.0636 53.4043 37.8298C53.4043 38.5959 53.7086 39.3307 54.2504 39.8725C54.7921 40.4142 55.5269 40.7186 56.2931 40.7186H97.2088C97.975 40.7186 98.7097 40.4142 99.2515 39.8725C99.7932 39.3307 100.098 38.5959 100.098 37.8298C100.098 37.0636 99.7932 36.3289 99.2515 35.7871C98.7097 35.2454 97.975 34.941 97.2088 34.941Z"
                fill="white"
            />
            <path
                d="M99.8208 46.7139C99.5883 46.2173 99.2191 45.7971 98.7565 45.5026C98.2939 45.2081 97.757 45.0514 97.2086 45.0509H56.2929C56.2304 45.0509 56.1709 45.0539 56.1084 45.0568C56.6422 45.3485 57.092 45.7727 57.4145 46.2885L60.2646 50.8255H97.2086C97.6945 50.8256 98.1725 50.703 98.5983 50.4691C99.0242 50.2352 99.384 49.8976 99.6446 49.4875C99.9051 49.0774 100.058 48.6082 100.089 48.1234C100.119 47.6385 100.027 47.1537 99.8208 46.7139Z"
                fill="white"
            />
            <path
                d="M122.816 57.6588C122.722 56.967 122.38 56.3328 121.855 55.8734C121.329 55.414 120.655 55.1604 119.957 55.1597H85.738L86.2497 55.9749L89.3675 60.9343H119.957C120.558 60.9356 121.144 60.7471 121.632 60.3958C122.006 60.1289 122.312 59.7764 122.522 59.3675C122.733 58.9587 122.843 58.5054 122.843 58.0455C122.843 57.9161 122.834 57.7869 122.816 57.6588Z"
                fill="white"
            />
            <path
                d="M132.728 36.8185L128.563 35.1912L107.797 27.0871L82.9523 17.3915C81.941 16.999 80.8154 17.0235 79.8222 17.4596C78.829 17.8957 78.0492 18.7079 77.6537 19.718L76.785 21.9463L74.7174 27.2389L71.7126 34.9413L69.4575 40.7188L67.7676 45.0505L65.5125 50.825L63.8197 55.1597L63.7722 55.2846L63.5015 55.9748L66.6193 60.9342L66.6551 60.9908H124.404C124.934 60.9903 125.457 61.1087 125.936 61.3373C126.414 61.5659 126.835 61.8989 127.167 62.3117L127.411 61.6929L130.392 54.0589L135.051 42.12C135.445 41.1088 135.421 39.9828 134.985 38.989C134.55 37.9952 133.738 37.2147 132.728 36.8185Z"
                fill="#D3D3D3"
            />
            <path
                d="M110.022 36.7414L107.797 35.8727L85.3593 27.1172C84.705 26.864 83.9771 26.8798 83.3345 27.1613C82.6918 27.4428 82.1866 27.967 81.929 28.6196C81.6754 29.2742 81.6914 30.0026 81.9736 30.6454C82.2557 31.2883 82.7809 31.7932 83.4344 32.0498L90.8453 34.9415L100.011 38.5176L107.797 41.558L108.097 41.6741C108.751 41.9279 109.479 41.9119 110.121 41.6297C110.763 41.3474 111.267 40.8219 111.522 40.1685C111.777 39.5152 111.762 38.7873 111.481 38.1448C111.2 37.5023 110.675 36.9976 110.022 36.7414Z"
                fill="white"
            />
            <path
                d="M118.971 54.1874L107.797 49.826L99.8209 46.7141L95.5605 45.0511L84.4636 40.7194L80.8578 39.3122C80.1852 39.0496 79.4386 39.0484 78.7652 39.3088C78.0917 39.5693 77.5402 40.0725 77.2194 40.7194C77.1838 40.7938 77.148 40.8711 77.1183 40.9515C76.9803 41.3046 76.9133 41.6815 76.921 42.0606C76.9287 42.4397 77.0111 42.8136 77.1633 43.1608C77.3155 43.5081 77.5347 43.822 77.8082 44.0845C78.0818 44.3471 78.4044 44.5532 78.7576 44.6911L79.6797 45.0511L94.4717 50.8256L107.797 56.026L116.871 59.5693C117.205 59.6997 117.56 59.7663 117.918 59.7657C118.592 59.7668 119.245 59.5324 119.764 59.1029C120.283 58.6734 120.636 58.0759 120.761 57.4138C120.886 56.7517 120.775 56.0668 120.448 55.4776C120.122 54.8884 119.599 54.432 118.971 54.1874Z"
                fill="white"
            />
            <path
                d="M107.797 60.6752L107.08 60.3955L100.068 57.6585L93.6625 55.1595L82.5537 50.8248L77.1808 48.7274C76.8273 48.5891 76.45 48.5219 76.0704 48.5295C75.6909 48.5371 75.3167 48.6195 74.969 48.7719C74.6214 48.9243 74.3072 49.1437 74.0444 49.4177C73.7816 49.6916 73.5754 50.0146 73.4376 50.3683C73.2998 50.722 73.233 51.0993 73.2411 51.4788C73.2493 51.8583 73.3321 52.2325 73.485 52.58C73.6378 52.9274 73.8577 53.2413 74.132 53.5037C74.4062 53.7661 74.7295 53.9719 75.0834 54.1093L77.7728 55.1595L91.1902 60.3956L92.5706 60.934L92.7135 60.9906H108.603L107.797 60.6752Z"
                fill="white"
            />
            <path
                d="M80.6557 60.9347L79.2752 60.3962L73.5066 58.1441C73.1036 57.9862 72.6702 57.9211 72.2386 57.9535C71.807 57.986 71.3882 58.1151 71.0133 58.3314C70.6384 58.5477 70.317 58.8456 70.0729 59.2031C69.8288 59.5605 69.6683 59.9683 69.6033 60.3962C69.5747 60.5742 69.5637 60.7545 69.5706 60.9347C69.57 60.9536 69.571 60.9725 69.5736 60.9912H80.8014L80.6557 60.9347Z"
                fill="white"
            />
            <path
                d="M92.5706 60.9341L91.1901 60.3956H69.6032C69.5746 60.5736 69.5637 60.754 69.5705 60.9341H66.6193L66.655 60.9906H92.7134L92.5706 60.9341ZM92.5706 60.9341H66.6193L66.655 60.9906H92.7134L92.5706 60.9341ZM92.5706 60.9341H66.6193L66.655 60.9906H92.7134L92.5706 60.9341ZM92.7134 60.9906L92.5706 60.9341H66.6193L66.655 60.9906H92.7134ZM40.6472 120.566C39.7055 120.564 38.8029 120.19 38.137 119.524C37.4712 118.858 37.0964 117.955 37.095 117.014V48.1801C37.0958 47.238 37.4702 46.3346 38.1361 45.6681C38.802 45.0017 39.7051 44.6265 40.6472 44.6249H46.1837V44.0299H40.6472C39.5474 44.032 38.4933 44.47 37.7159 45.2479C36.9385 46.0259 36.5013 47.0803 36.5 48.1801V117.014C36.5009 117.809 36.7306 118.588 37.1615 119.257C37.5924 119.926 38.2065 120.456 38.9306 120.786H91.3449C91.2974 120.715 91.2497 120.64 91.2021 120.566H40.6472ZM127.411 61.6927C127.025 61.2828 126.559 60.9562 126.042 60.7331C125.524 60.5101 124.967 60.3952 124.404 60.3956H66.9852L63.7722 55.2845L63.6948 55.1595L60.9698 50.8249L57.9173 45.9696C57.5432 45.3765 57.0251 44.8877 56.4114 44.5485C55.7976 44.2094 55.108 44.031 54.4068 44.0299H40.6472C39.5474 44.032 38.4933 44.47 37.7159 45.2479C36.9385 46.0259 36.5013 47.0803 36.5 48.1801V117.014C36.5009 117.809 36.7306 118.588 37.1615 119.257C37.5924 119.926 38.2065 120.456 38.9306 120.786C39.4689 121.034 40.0546 121.162 40.6472 121.161H124.404C124.997 121.162 125.584 121.034 126.123 120.786C126.847 120.456 127.461 119.926 127.892 119.257C128.323 118.588 128.553 117.809 128.554 117.014V64.5458C128.555 63.4832 128.145 62.4612 127.411 61.6927ZM127.959 117.014C127.957 117.956 127.582 118.859 126.916 119.525C126.249 120.191 125.346 120.565 124.404 120.566H40.6472C39.7055 120.564 38.8029 120.19 38.137 119.524C37.4712 118.858 37.0964 117.955 37.095 117.014V48.1801C37.0958 47.238 37.4702 46.3346 38.1361 45.6681C38.802 45.0017 39.7051 44.6265 40.6472 44.6249H54.4068C55.0082 44.6233 55.6001 44.7752 56.1264 45.0662C56.6528 45.3572 57.0961 45.7777 57.4145 46.288L60.2646 50.8249L62.9898 55.1595L63.5014 55.9747L66.6193 60.9341L66.655 60.9906L124.404 60.9906C125.346 60.9914 126.25 61.3662 126.917 62.0328C127.583 62.6993 127.958 63.6031 127.959 64.5458V117.014ZM92.5706 60.9341H66.6193L66.655 60.9906H92.7134L92.5706 60.9341ZM92.5706 60.9341H66.6193L66.655 60.9906H92.7134L92.5706 60.9341ZM92.5706 60.9341H66.6193L66.655 60.9906H92.7134L92.5706 60.9341ZM92.5706 60.9341L91.1901 60.3956H66.9852L63.7722 55.2845L63.5014 55.9747L66.6193 60.9341L66.655 60.9906H92.7134L92.5706 60.9341Z"
                fill="#0E0E0E"
            />
            <path
                d="M82.1489 73.0006C80.7766 73.0289 79.683 74.1621 79.7087 75.542V99.4933L74.0086 93.7567C73.7502 93.4864 73.4346 93.2782 73.0853 93.1473C72.7359 93.0164 72.3617 92.9662 71.9905 93.0006C71.5193 93.0476 71.0712 93.2292 70.6992 93.5237C70.3272 93.8183 70.0466 94.2137 69.8906 94.6633C69.7346 95.1129 69.7097 95.5979 69.8187 96.0613C69.9277 96.5246 70.1662 96.947 70.506 97.2786L80.4413 107.274C80.9067 107.739 81.5363 108 82.1925 108C82.8488 108 83.4784 107.739 83.9438 107.274L93.8839 97.2786C94.1439 97.0547 94.3551 96.7791 94.5041 96.4692C94.6532 96.1594 94.7369 95.8218 94.7501 95.4779C94.7633 95.1339 94.7056 94.7909 94.5808 94.4704C94.4559 94.1499 94.2665 93.8588 94.0244 93.6154C93.7823 93.372 93.4928 93.1816 93.1741 93.056C92.8554 92.9304 92.5143 92.8724 92.1722 92.8857C91.8301 92.8989 91.4945 92.9832 91.1863 93.133C90.8781 93.2829 90.6041 93.4952 90.3814 93.7567L84.6764 99.4786V75.542C84.7028 74.126 83.5571 72.9739 82.1489 73.0006Z"
                fill="#7B67FB"
            />
            <path
                d="M221.433 121.084C221.432 121.163 221.401 121.238 221.345 121.294C221.29 121.35 221.214 121.381 221.135 121.381H8.71694C8.63804 121.381 8.56236 121.35 8.50657 121.294C8.45078 121.238 8.41943 121.163 8.41943 121.084C8.41943 121.005 8.45078 120.929 8.50657 120.873C8.56236 120.818 8.63804 120.786 8.71694 120.786H221.135C221.214 120.786 221.29 120.818 221.345 120.874C221.401 120.929 221.432 121.005 221.433 121.084Z"
                fill="#3F3D56"
            />
        </g>
        <defs>
            <clipPath id="clip0_3378_75846">
                <rect
                    width="231.161"
                    height="121.381"
                    fill="white"
                    transform="translate(0.419434 0.000106812)"
                />
            </clipPath>
        </defs>
    </svg>
);

const IconImport = (
    <svg
        width="232"
        height="122"
        viewBox="0 0 232 122"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <g clipPath="url(#clip0_3378_75870)">
            <path
                d="M228.09 88.4778C226.879 96.1742 223.127 103.645 216.936 108.372C210.843 113.028 203.037 114.682 195.501 116.104C186.463 117.812 177.4 119.373 168.312 120.787C167.024 120.992 165.735 121.19 164.445 121.382H111.513C111.459 121.31 111.402 121.236 111.349 121.162C111.263 121.037 111.176 120.912 111.093 120.787C111.045 120.715 110.998 120.641 110.95 120.567H144.152C145.094 120.566 145.997 120.191 146.664 119.525C147.33 118.86 147.705 117.957 147.707 117.014V72.6981C147.921 72.0258 148.12 71.3504 148.302 70.6662C149.149 67.4799 149.699 64.2223 149.947 60.9348C150.126 58.644 150.179 56.3532 150.14 54.0595C150.031 47.7554 149.206 41.4512 148.311 35.1917C148.245 34.7306 148.177 34.2665 148.112 33.8054C147.481 29.4559 146.907 24.7226 149.099 20.9115C151.607 16.5501 157.174 14.7323 162.187 15.1786C171.204 15.9848 178.799 22.8899 182.747 31.0386C186.692 39.1843 187.576 48.4485 187.935 57.4927C188.126 62.2885 188.105 67.2984 187.436 72.1091C189.697 66.9385 191.96 61.7678 194.225 56.5972C195.433 53.8393 196.703 50.9922 198.94 48.9751C203.617 44.7535 211.352 45.6044 216.347 49.4482C221.339 53.2949 224.076 59.3729 225.897 65.4033C228.152 72.8617 229.304 80.7813 228.09 88.4778Z"
                fill="#F2F2F2"
            />
            <path
                d="M208.141 46.6743C211.412 52.4662 212.912 59.1077 212.876 65.7353C212.825 72.3064 211.372 78.7908 208.612 84.7546C206.117 90.1396 202.639 95.0124 198.358 99.1237C194.134 103.226 189.121 106.428 183.623 108.537C177.243 110.928 170.343 111.584 163.627 110.437C162.694 110.277 161.768 110.082 160.849 109.854C160.291 109.716 160.053 110.577 160.611 110.714C167.6 112.447 174.929 112.236 181.806 110.105C187.534 108.262 192.82 105.255 197.331 101.273C201.898 97.2801 205.675 92.4651 208.465 87.0784C211.519 81.1809 213.3 74.7081 213.694 68.0788C214.084 61.3517 212.956 54.5048 210.047 48.4025C209.694 47.6631 209.316 46.9368 208.912 46.2238C208.85 46.126 208.753 46.0559 208.64 46.0285C208.528 46.001 208.409 46.0183 208.309 46.0768C208.209 46.1352 208.136 46.2302 208.104 46.3416C208.073 46.4531 208.086 46.5724 208.141 46.6743Z"
                fill="white"
            />
            <path
                d="M160.776 15.0607C164.402 21.7448 167.228 28.8329 169.195 36.1783C173.132 50.8902 173.725 66.2972 170.93 81.2682C169.534 88.748 167.259 96.0365 164.15 102.981C161.05 109.868 156.588 117.124 148.965 119.535C148.104 119.806 147.223 120.003 146.329 120.124C145.762 120.202 146.004 121.062 146.566 120.985C154.451 119.902 159.931 113.351 163.367 106.674C166.922 99.764 169.454 92.2607 171.138 84.6853C174.475 69.5698 174.355 53.896 170.787 38.8334C169.014 31.3298 166.365 24.0607 162.894 17.1761C162.458 16.3139 162.009 15.4586 161.547 14.6102C161.272 14.1055 160.501 14.5555 160.776 15.0607Z"
                fill="white"
            />
            <path
                d="M213.433 121.084C213.432 121.163 213.401 121.238 213.345 121.294C213.29 121.35 213.214 121.381 213.135 121.381H0.716938C0.638035 121.381 0.562363 121.35 0.50657 121.294C0.450778 121.239 0.419434 121.163 0.419434 121.084C0.419434 121.005 0.450778 120.929 0.50657 120.874C0.562363 120.818 0.638035 120.786 0.716938 120.786H213.135C213.214 120.787 213.29 120.818 213.345 120.874C213.401 120.93 213.432 121.005 213.433 121.084Z"
                fill="#3F3D56"
            />
            <path
                d="M46.1872 35.1924L108.666 23.3197C108.763 23.3013 108.863 23.3655 108.884 23.473L124.938 107.957C124.958 108.064 124.888 108.161 124.791 108.18L62.3131 120.052C62.2161 120.071 62.1155 120.006 62.0951 119.899L46.0406 35.415C46.0202 35.3075 46.0902 35.2109 46.1872 35.1924Z"
                fill="#F9F9F9"
                stroke="#CACACA"
                strokeWidth="0.863784"
            />
            <path
                d="M103.132 44.2992L58.2438 52.8294C57.9401 52.8871 57.6252 52.8182 57.3684 52.6378C57.1116 52.4574 56.934 52.1803 56.8745 51.8675C56.8151 51.5547 56.8787 51.2318 57.0514 50.9698C57.2241 50.7078 57.4918 50.5281 57.7955 50.4704L102.684 41.9403C102.988 41.8826 103.303 41.9515 103.56 42.1319C103.816 42.3122 103.994 42.5893 104.053 42.9021C104.113 43.2149 104.049 43.5379 103.877 43.7999C103.704 44.0619 103.436 44.2415 103.132 44.2992Z"
                fill="#DCDCDC"
            />
            <path
                d="M104.477 51.6021L59.5887 60.1323C59.285 60.19 58.9702 60.1211 58.7134 59.9407C58.4566 59.7603 58.2789 59.4832 58.2195 59.1704C58.1601 58.8576 58.2237 58.5347 58.3964 58.2727C58.5691 58.0107 58.8368 57.831 59.1405 57.7733L104.029 49.2432C104.333 49.1855 104.648 49.2544 104.905 49.4348C105.161 49.6151 105.339 49.8922 105.398 50.205C105.458 50.5179 105.394 50.8408 105.222 51.1028C105.049 51.3648 104.781 51.5444 104.477 51.6021Z"
                fill="#DCDCDC"
            />
            <path
                d="M105.972 59.015L61.0829 67.5451C60.7792 67.6028 60.4643 67.5339 60.2075 67.3535C59.9507 67.1731 59.7731 66.8961 59.7136 66.5833C59.6542 66.2704 59.7178 65.9475 59.8905 65.6855C60.0633 65.4235 60.3309 65.2439 60.6346 65.1862L105.523 56.656C105.827 56.5983 106.142 56.6672 106.399 56.8476C106.655 57.028 106.833 57.3051 106.893 57.6179C106.952 57.9307 106.888 58.2536 106.716 58.5156C106.543 58.7776 106.275 58.9572 105.972 59.015Z"
                fill="#DCDCDC"
            />
            <path
                d="M107.167 66.4278L62.2782 74.958C61.9745 75.0157 61.6596 74.9468 61.4028 74.7664C61.146 74.586 60.9684 74.3089 60.9089 73.9961C60.8495 73.6833 60.9131 73.3604 61.0859 73.0983C61.2586 72.8363 61.5262 72.6567 61.8299 72.599L106.719 64.0689C107.022 64.0111 107.337 64.0801 107.594 64.2604C107.851 64.4408 108.028 64.7179 108.088 65.0307C108.147 65.3435 108.084 65.6665 107.911 65.9285C107.738 66.1905 107.471 66.3701 107.167 66.4278Z"
                fill="#DCDCDC"
            />
            <path
                d="M108.661 73.8406L63.7726 82.3708C63.4689 82.4285 63.154 82.3596 62.8972 82.1792C62.6404 81.9988 62.4628 81.7218 62.4033 81.4089C62.3439 81.0961 62.4075 80.7732 62.5802 80.5112C62.753 80.2492 63.0206 80.0696 63.3243 80.0119L108.213 71.4817C108.517 71.424 108.832 71.4929 109.088 71.6733C109.345 71.8537 109.523 72.1307 109.582 72.4436C109.642 72.7564 109.578 73.0793 109.405 73.3413C109.233 73.6033 108.965 73.7829 108.661 73.8406Z"
                fill="#DCDCDC"
            />
            <path
                d="M112.2 89.8292L83.0222 95.3737C82.6111 95.4519 82.1775 94.9871 82.0537 94.3357C81.9299 93.6843 82.1629 93.0929 82.574 93.0148L111.752 87.4702C112.163 87.3921 112.596 87.8568 112.72 88.5082C112.844 89.1596 112.611 89.751 112.2 89.8292Z"
                fill="#E6E6E6"
            />
            <path
                d="M113.545 96.9058L84.3672 102.45C83.9561 102.529 83.5225 102.064 83.3987 101.412C83.2749 100.761 83.5079 100.17 83.9189 100.091L113.096 94.5469C113.508 94.4688 113.941 94.9335 114.065 95.5849C114.189 96.2363 113.956 96.8277 113.545 96.9058Z"
                fill="#E6E6E6"
            />
            <path
                d="M73.0864 20.5001H146.712C146.826 20.5001 146.927 20.5949 146.927 20.7215V120.279C146.927 120.405 146.826 120.5 146.712 120.5H73.0864C72.9721 120.5 72.8716 120.405 72.8716 120.279V20.7215C72.8716 20.5949 72.9721 20.5001 73.0864 20.5001Z"
                fill="white"
                stroke="#CACACA"
            />
            <path
                d="M136.357 103.835H101.974C101.489 103.835 101.096 103.213 101.096 102.445C101.096 101.678 101.489 101.055 101.974 101.055H136.357C136.841 101.055 137.234 101.678 137.234 102.445C137.234 103.213 136.841 103.835 136.357 103.835Z"
                fill="#E6E6E6"
            />
            <path
                d="M136.357 111.248H101.974C101.489 111.248 101.096 110.625 101.096 109.858C101.096 109.09 101.489 108.468 101.974 108.468H136.357C136.841 108.468 137.234 109.09 137.234 109.858C137.234 110.625 136.841 111.248 136.357 111.248Z"
                fill="#E6E6E6"
            />
            <path
                d="M110.5 91.0001C121.546 91.0001 130.5 82.0458 130.5 71.0001C130.5 59.9544 121.546 51.0001 110.5 51.0001C99.4543 51.0001 90.5 59.9544 90.5 71.0001C90.5 82.0458 99.4543 91.0001 110.5 91.0001Z"
                fill="#6C63FF"
            />
            <path
                d="M101.799 70.2026C101.454 70.2026 101.124 70.3395 100.88 70.5831C100.637 70.8268 100.5 71.1572 100.5 71.5018C100.5 71.8463 100.637 72.1768 100.88 72.4204C101.124 72.664 101.454 72.8009 101.799 72.8009H108.701V79.7041C108.702 80.0481 108.839 80.3777 109.083 80.6207C109.326 80.8637 109.656 81.0001 110 81.0001C110.344 81.0001 110.674 80.8637 110.917 80.6207C111.161 80.3777 111.298 80.0481 111.299 79.7041V72.8009H118.201C118.546 72.8009 118.876 72.664 119.12 72.4204C119.363 72.1768 119.5 71.8463 119.5 71.5018C119.5 71.1572 119.363 70.8268 119.12 70.5831C118.876 70.3395 118.546 70.2026 118.201 70.2026H111.299V63.2991C111.299 62.9546 111.162 62.6242 110.918 62.3806C110.675 62.137 110.344 62.0001 110 62.0001C109.656 62.0001 109.325 62.137 109.082 62.3806C108.838 62.6242 108.701 62.9546 108.701 63.2991V70.2026H101.799Z"
                fill="white"
            />
            <path
                d="M37.6488 109.836C38.4398 110.113 39.2891 110.183 40.1148 110.038C40.9406 109.893 41.715 109.538 42.3634 109.007C44.0147 107.625 44.532 105.347 44.9536 103.239L46.1994 97.0001L43.5912 98.7912C41.7153 100.08 39.797 101.41 38.4981 103.276C37.1993 105.143 36.6323 107.69 37.676 109.71"
                fill="#E6E6E6"
            />
            <path
                d="M38.0518 117.822C37.7227 115.436 37.3857 113.019 37.6164 110.607C37.8209 108.465 38.4759 106.373 39.8096 104.658C40.5175 103.749 41.3789 102.97 42.355 102.357C42.6101 102.197 42.844 102.599 42.5903 102.759C40.9014 103.824 39.5951 105.396 38.8597 107.249C38.0475 109.31 37.917 111.556 38.0571 113.746C38.1421 115.07 38.3212 116.385 38.5019 117.699C38.5161 117.758 38.5073 117.821 38.477 117.874C38.4468 117.927 38.3975 117.967 38.339 117.985C38.2794 118.001 38.2158 117.993 38.162 117.963C38.1083 117.932 38.0688 117.882 38.0522 117.822H38.0518Z"
                fill="#F2F2F2"
            />
            <path
                d="M24.1284 114.258C24.5313 114.733 25.0499 115.096 25.6344 115.313C26.2188 115.53 26.8494 115.592 27.4653 115.495C29.0458 115.228 30.2369 113.961 31.3022 112.766L34.4542 109.232L32.1051 109.402C30.4159 109.525 28.6838 109.659 27.1468 110.369C25.6099 111.079 24.2855 112.5 24.1937 114.188"
                fill="#E6E6E6"
            />
            <path
                d="M21.424 119.187C21.9778 117.857 22.5363 116.509 23.3861 115.334C24.1151 114.27 25.1328 113.435 26.3201 112.927C26.9607 112.672 27.6409 112.53 28.3303 112.509C28.5102 112.503 28.5091 112.781 28.3303 112.787C27.1364 112.826 25.986 113.243 25.0449 113.977C24.0012 114.796 23.253 115.915 22.6616 117.086C22.3039 117.794 21.9978 118.527 21.6929 119.259C21.6823 119.294 21.6588 119.323 21.6273 119.342C21.5957 119.36 21.5584 119.365 21.5228 119.357C21.4872 119.348 21.4568 119.324 21.4383 119.292C21.4198 119.261 21.4147 119.223 21.424 119.187Z"
                fill="#F2F2F2"
            />
            <path
                d="M40.4148 113.994C40.754 114.508 41.2204 114.927 41.7689 115.209C42.3174 115.491 42.9295 115.628 43.5463 115.606C45.1315 115.531 46.4531 114.427 47.6426 113.379L51.1613 110.28L48.8326 110.168C47.1579 110.089 45.44 110.014 43.8448 110.529C42.2496 111.044 40.7786 112.284 40.4869 113.931"
                fill="#E6E6E6"
            />
            <path
                d="M37.1234 119.194C38.704 116.404 40.5375 113.303 43.8134 112.312C44.7242 112.038 45.6793 111.939 46.6272 112.022C46.926 112.048 46.8513 112.507 46.5533 112.482C44.9644 112.35 43.3785 112.769 42.0628 113.667C40.7967 114.527 39.8108 115.722 38.9763 116.99C38.4661 117.766 38.0075 118.575 37.5495 119.384C37.4032 119.642 36.9755 119.456 37.1234 119.194Z"
                fill="#F2F2F2"
            />
            <path
                d="M94.7756 42.0481C94.7756 41.631 94.889 41.3409 95.1156 41.1777C95.3423 41.0054 95.6188 40.9193 95.9452 40.9193C96.2626 40.9193 96.5346 41.0054 96.7612 41.1777C96.9879 41.3409 97.1012 41.631 97.1012 42.0481C97.1012 42.447 96.9879 42.7372 96.7612 42.9185C96.5346 43.0908 96.2626 43.1769 95.9452 43.1769C95.6188 43.1769 95.3423 43.0908 95.1156 42.9185C94.889 42.7372 94.7756 42.447 94.7756 42.0481Z"
                fill="#95A0A8"
            />
            <path
                d="M100.463 39.2057L98.069 35.5745H100.367L101.809 37.9409L103.264 35.5745H105.563L103.142 39.2057L105.671 43.0001H103.373L101.809 40.4569L100.245 43.0001H97.9466L100.463 39.2057Z"
                fill="#95A0A8"
            />
            <path
                d="M108.828 43.0001H106.802V32.6641H108.828V43.0001Z"
                fill="#95A0A8"
            />
            <path
                d="M112.469 39.2057L110.075 35.5745H112.374L113.815 37.9409L115.27 35.5745H117.569L115.148 39.2057L117.678 43.0001H115.379L113.815 40.4569L112.251 43.0001H109.953L112.469 39.2057Z"
                fill="#95A0A8"
            />
            <path
                d="M123.99 40.7969C123.99 41.2956 123.872 41.7217 123.636 42.0753C123.4 42.4198 123.047 42.6828 122.575 42.8641C122.104 43.0454 121.519 43.1361 120.821 43.1361C120.304 43.1361 119.86 43.0998 119.488 43.0273C119.116 42.9638 118.745 42.855 118.373 42.7009V41.0281C118.772 41.2094 119.203 41.359 119.665 41.4769C120.127 41.5948 120.535 41.6537 120.889 41.6537C121.288 41.6537 121.569 41.5948 121.732 41.4769C121.904 41.359 121.991 41.2049 121.991 41.0145C121.991 40.8876 121.954 40.7742 121.882 40.6745C121.818 40.5748 121.673 40.4614 121.447 40.3345C121.22 40.2076 120.866 40.0444 120.386 39.8449C119.923 39.6454 119.543 39.4505 119.243 39.2601C118.944 39.0606 118.722 38.8249 118.577 38.5529C118.432 38.2718 118.359 37.9228 118.359 37.5057C118.359 36.8166 118.627 36.2998 119.162 35.9553C119.697 35.6108 120.408 35.4385 121.297 35.4385C121.759 35.4385 122.199 35.4838 122.616 35.5745C123.033 35.6652 123.464 35.8148 123.908 36.0233L123.296 37.4785C123.051 37.3697 122.816 37.2745 122.589 37.1929C122.362 37.1113 122.14 37.0478 121.923 37.0025C121.714 36.9572 121.496 36.9345 121.27 36.9345C120.971 36.9345 120.744 36.9753 120.59 37.0569C120.445 37.1385 120.372 37.2609 120.372 37.4241C120.372 37.542 120.408 37.6508 120.481 37.7505C120.563 37.8412 120.712 37.9409 120.93 38.0497C121.156 38.1585 121.487 38.3036 121.923 38.4849C122.349 38.6572 122.716 38.8385 123.024 39.0289C123.332 39.2102 123.568 39.4414 123.731 39.7225C123.904 39.9945 123.99 40.3526 123.99 40.7969Z"
                fill="#95A0A8"
            />
        </g>
        <defs>
            <clipPath id="clip0_3378_75870">
                <rect
                    width="231.161"
                    height="121.381"
                    fill="white"
                    transform="translate(0.419434 0.000106812)"
                />
            </clipPath>
        </defs>
    </svg>
);
